import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';

import { TranslateModule } from '@ngx-translate/core';
import { StructuresService } from '@rp/core';
import { ButtonComponent, DialogService, IconComponent, IconName } from '@rp/shared/components';
import { DateFormat, Entity, StructureResponse } from '@rp/shared/models';
import { HeaderStateService } from '@rp/shared/services';
import { CentsToDollarsPipe } from '@rp/shared/pipes';
import { take } from 'rxjs';

import { DetailsDialogComponent, FinanceStatusComponent } from '../';
import { Payout } from '../../models';
import { FINANCE_WEBMASTER_PROVIDER_TOKEN, HttpFinanceWebmasterProvider } from '../../providers';

@Component({
  selector: 'rp-details-page',
  standalone: true,
  templateUrl: './details-page.component.html',
  styleUrl: './details-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    IconComponent,
    ButtonComponent,
    FinanceStatusComponent,
    DatePipe,
    CentsToDollarsPipe,
  ],
  providers: [
    { provide: FINANCE_WEBMASTER_PROVIDER_TOKEN, useClass: HttpFinanceWebmasterProvider },
  ],
})
export class DetailsPageComponent implements OnInit {
  icons = IconName;
  DateFormat = DateFormat;

  payout = signal<Payout>(null);
  structures = signal<StructureResponse>(null);
  payoutStatuses = signal<Map<number, string>>(null);

  dialogService = inject(DialogService);
  activatedRoute = inject(ActivatedRoute);
  structuresService = inject(StructuresService);

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _headerStateService = inject(HeaderStateService);
  private readonly _router = inject(Router);

  ngOnInit(): void {
    this._setHeaderState();

    const params = this.activatedRoute.snapshot.queryParams;
    this.payout.set({ ...params, status: +params['status'] } as Payout);

    this.structuresService
      .getStructures()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((structures: StructureResponse) => {
        const { payoutStatuses } = structures;
        this.structures.set(structures);
        this.payoutStatuses.set(
          new Map(
            payoutStatuses.map((payoutStatus: Entity) => [payoutStatus.id, payoutStatus.name]),
          ),
        );
      });
  }

  openDetails(): void {
    this.dialogService.open(DetailsDialogComponent, {
      statuses: this.payoutStatuses,
      structures: this.structures,
      payout: this.payout(),
    });
  }

  private _setHeaderState(): void {
    this._headerStateService.title.set('finance.payout');
    this._headerStateService.showBack.set(true);
    this._headerStateService.backClick.pipe(take(1)).subscribe(() => {
      this._router.navigate(['../'], { relativeTo: this.activatedRoute });
    });
  }
}
